import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { LexicalTypeaheadMenuPlugin } from "@lexical/react/LexicalTypeaheadMenuPlugin";
import type { LexicalNode } from "lexical";
import type { FC } from "react";
import { useCallback, useState } from "react";
import ReactDOM from "react-dom";
import type { MergeTagLookupProps } from "../../../../merge-tag/components/merge-tag-lookup";
import { MergeTagLookup } from "../../../../merge-tag/components/merge-tag-lookup";
import type { TagGroup } from "../../../../merge-tag/components/types";
import type { MergeTagV1 } from "../../../../merge-tag/types";
import { useLexicalContext } from "../../lexical-context";
import { $createMergeTagV2Node } from "../../nodes/merge-tag-node-v2";
import { MergeTagTypeaheadOption } from "../../types/merge-tag-typeahead-option";
import { checkForCurlyBraces } from "./merge-tag-utils";
import { useAutoMergeTag } from "./use-auto-merge-tag";
import { useMergeTagV1LookupService } from "./use-merge-tag-lookup-service";

export const MergeTagTypeaheadV2Plugin: FC<{ v1Tags: MergeTagV1[] }> = ({ v1Tags }) => {
  const { filterTags } = useLexicalContext();
  const [editor] = useLexicalComposerContext();

  const onSelectOption = useCallback(
    (
      selectedOption: MergeTagTypeaheadOption,
      nodeToReplace: LexicalNode | null,
      closeMenu: () => void,
    ) => {
      editor.update(() => {
        const node = $createMergeTagV2Node(selectedOption.key);
        if (node) {
          nodeToReplace?.replace(node);
          node.selectNext();
        }
        closeMenu();
      });
    },
    [editor],
  );

  return (
    <GenericMergeTagTypeahead
      filterTags={filterTags}
      createMergeTagNode={$createMergeTagV2Node}
      onSelectOption={onSelectOption}
      v1Tags={v1Tags}
    />
  );
};

type GenericMergeTagTypeaheadProps = {
  filterTags: (x: string) => TagGroup;
  createMergeTagNode: (tagId: string) => LexicalNode | null;
  onSelectOption: (
    selected: MergeTagTypeaheadOption,
    nodeToReplace: LexicalNode | null,
    closeMenu: () => void,
  ) => void;
  v1Tags: MergeTagV1[];
};

const GenericMergeTagTypeahead: FC<GenericMergeTagTypeaheadProps> = ({
  filterTags,
  createMergeTagNode,
  onSelectOption,
  v1Tags,
}) => {
  const [queryString, setQueryString] = useState<string | null>(null);
  const results = useMergeTagV1LookupService(v1Tags, queryString);
  useAutoMergeTag(createMergeTagNode);

  return (
    <LexicalTypeaheadMenuPlugin<MergeTagTypeaheadOption>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      options={results}
      triggerFn={checkForCurlyBraces}
      menuRenderFn={(anchorElementRef, renderProps) => {
        return anchorElementRef.current
          ? ReactDOM.createPortal(
              <MergeTagLexicalTypeahead
                filterTags={filterTags}
                onSelection={(x) => {
                  return renderProps.selectOptionAndCleanUp(new MergeTagTypeaheadOption(x));
                }}
                anchorEl={anchorElementRef.current}
              />,
              anchorElementRef.current,
            )
          : null;
      }}
    />
  );
};

const MergeTagLexicalTypeahead: FC<
  Pick<MergeTagLookupProps, "filterTags" | "onSelection"> & { anchorEl: HTMLElement }
> = ({ filterTags, onSelection, anchorEl }) => {
  const [open, setOpen] = useState(true);

  return (
    <MergeTagLookup
      open={open}
      filterTags={filterTags}
      onSelection={onSelection}
      anchorEl={anchorEl}
      onClose={() => setOpen(false)}
    />
  );
};
